import { __decorate, __metadata } from '../../node_modules/tslib/tslib.es6.js';
import '../../node_modules/lit/node_modules/@lit/reactive-element/reactive-element.js';
import { html as x, nothing as A } from '../../node_modules/lit-html/lit-html.js';
import '../../node_modules/lit-element/lit-element.js';
import { property as n } from '../../node_modules/lit/node_modules/@lit/reactive-element/decorators/property.js';
import '../../node_modules/lit/node_modules/@lit/reactive-element/decorators/query-assigned-elements.js';
import { ifDefined as l } from '../../node_modules/lit-html/directives/if-defined.js';
import getClassName from '../../node_modules/@fmr-ap142296/brick-helpers/dist/src/functions/get-class-name.js';
import { ScopedElementsMixin } from '../utilities/scoped-elements/scoped-elements.mixin.js';
import { PvdSlotifyBaseComponent } from '../pvd-base-component/pvd-slotify-base-component.js';
import { StandardSize, Size } from '../pvd-utils/enums/size.js';

const DEFAULT_DISABLED = false;
const DEFAULT_SIZE = undefined;
const DEFAULT_HREF = `#`;
const DEFAULT_EXTERNAL_LINK_ICON = 'action__open-link';
const DEFAULT_NEW_WINDOW_STRING = 'Opens in a new window';
const LINK_ANCHOR_PROTECTED_ATTR_MAP = new Map([
    ['aria-describedby', 'pvd-aria-describedby'],
    ['aria-label', 'pvd-aria-label'],
    ['aria-disabled', 'pvd-disabled'],
    ['class', 'pvd-class'],
    ['disabled', 'pvd-disabled'],
    ['id', 'pvd-id'],
    ['rel', 'pvd-new-window'],
    ['target', 'pvd-new-window'],
]);
const LINK_BUTTON_PROTECTED_ATTR_MAP = new Map([
    ['aria-describedby', 'pvd-aria-describedby'],
    ['aria-label', 'pvd-aria-label'],
    ['aria-disabled', 'pvd-disabled'],
    ['class', 'pvd-class'],
    ['disabled', 'pvd-disabled'],
    ['id', 'pvd-id'],
]);
/**
 * Providence Link component.
 * @see {@link https://providence.fmr.com/4.x/components/link/link.html|Link documentation}
 */
class PvdLink extends ScopedElementsMixin(PvdSlotifyBaseComponent) {
    constructor() {
        super(...arguments);
        this.pvdDisabled = DEFAULT_DISABLED;
        this.pvdSize = DEFAULT_SIZE;
        this.pvdHref = DEFAULT_HREF;
        // #endregion
    }
    static get scopedElements() {
        // $namespace-$name-$extendingComponentName
        return {
            'pvd-scoped-icon': customElements.get(`${this.namespace}-icon`),
        };
    }
    // #endregion
    // #region public methods
    /**
     * Overrides focus method so that focus is given to the native button or link
     * element.
     *
     * @param options - options that are passed through unmodified to the native
     * `focus` method
     */
    focus(options) {
        const linkEl = this.querySelector('.pvd-link__link');
        if (linkEl) {
            linkEl.focus(options);
        }
    }
    // #endregion
    // #region private methods
    /**
     * Sets attributes on the link element that are coming from pvdNativeAnchorAttributes
     * @param prevAttr optional, if user updates pvdNativeAnchorAttributes, this will remove/update existing attributes
     */
    setNativeAnchorAttributes(prevAttr) {
        const targetElement = this.querySelector('.pvd-link__link');
        this.setNativeAttributes(this.pvdNativeAnchorAttributes, LINK_ANCHOR_PROTECTED_ATTR_MAP, targetElement, prevAttr);
    }
    /**
     * Sets attributes on the button element that are coming from pvdNativeButtonAttributes
     * @param prevAttr optional, if user updates pvdNativeButtonAttributes, this will remove/update existing attributes
     */
    setNativeButtonAttributes(prevAttr) {
        const targetElement = this.querySelector('.pvd-link__link');
        this.setNativeAttributes(this.pvdNativeButtonAttributes, LINK_BUTTON_PROTECTED_ATTR_MAP, targetElement, prevAttr);
    }
    /**
     * Determines if the consumer provided a slotted icon-left OR icon-right
     */
    get hasIcon() {
        return this.hasIconLeft || this.hasIconRight;
    }
    /**
     * Determines if the consumer provided a slotted icon-left
     */
    get hasIconLeft() {
        return !!this.pvdIconLeft || this.isSlotUsed('icon-left');
    }
    /**
     * Determines if the consumer provided a slotted icon-right
     */
    get hasIconRight() {
        return !!this.pvdIconRight || this.isSlotUsed('icon-right');
    }
    /**
     * Determines if the consumer provided pvdText or slotted text
     */
    get hasText() {
        return !!this.pvdText || this.isSlotUsed();
    }
    /**
     * Determines if there is an icon without text
     */
    get iconOnly() {
        return !this.hasText && this.hasIcon;
    }
    // #endregion
    // #region LitElement lifecycles
    updated(changedProperties) {
        super.updated(changedProperties);
        if (changedProperties.has('pvdNewWindow') && !this.hasAttribute('pvd-new-window')) {
            this.pvdIconRight = undefined;
        }
        if (this.pvdNativeAnchorAttributes) {
            const prevAttr = changedProperties.get('pvdNativeAnchorAttributes');
            // if native attributes are being set, go through the logic of applying attributes to target element
            this.setNativeAnchorAttributes(prevAttr);
        }
        if (this.pvdNativeButtonAttributes) {
            const prevAttr = changedProperties.get('pvdNativeButtonAttributes');
            // if native attributes are being set, go through the logic of applying attributes to target element
            this.setNativeButtonAttributes(prevAttr);
        }
    }
    renderDisabledCover() {
        return x `${this.pvdDisabled ? x `<span class="pvd-link__disabled-cover"></span>` : A}`;
    }
    renderLeftIcon() {
        return this.hasIconLeft
            ? x `<span class="pvd-link__icon pvd-link__icon--left"><s-slot name="icon-left">${this.renderIcon(this.pvdIconLeft)}</s-slot></span>`
            : x `<s-slot name="icon-left"></s-slot>`;
    }
    renderRightIcon() {
        if (!this.hasIconRight && this.hasAttribute('pvd-new-window')) {
            this.pvdIconRight = DEFAULT_EXTERNAL_LINK_ICON;
        }
        else if (!this.hasIconRight && !this.hasAttribute('pvd-new-window')) {
            this.pvdIconRight = undefined;
        }
        return this.hasIconRight
            ? x `<span class="pvd-link__icon pvd-link__icon--right"><s-slot name="icon-right">${this.renderIcon(this.pvdIconRight)}</s-slot></span>`
            : x `<s-slot name="icon-right"></s-slot>`;
    }
    renderIcon(iconName) {
        return this.scopeTemplate `<pvd-scoped-icon
      pvd-name="${iconName}"
      pvd-size="${this.pvdSize === StandardSize.large ? Size.medium : Size.small}"
    ></pvd-scoped-icon>`;
    }
    renderText() {
        return this.hasText
            ? x `<span class="pvd-link__text"><s-slot>${this.pvdText}</s-slot></span>`
            : x `<s-slot></s-slot>`;
    }
    renderNewWindowString() {
        return this.hasAttribute('pvd-new-window')
            ? x `<span class="pvd-link__screen-reader-text">${this.pvdNewWindow}</span>`
            : A;
    }
    renderLinkContents() {
        // unable to dynamically create an named s-slot (e.g., <s-slot name="icon-${position}"></s-slot> doesn't work)
        const leftIcon = this.renderLeftIcon();
        const rightIcon = this.renderRightIcon();
        const text = this.renderText();
        const newWindowString = this.renderNewWindowString();
        return x `${leftIcon}${text}${rightIcon}${newWindowString}`;
    }
    renderLink() {
        return x `${this.pvdTrigger
            ? x `${this.renderDisabledCover()} <button type="button" class="pvd-link__link${this.pvdClass ? ` ${this.pvdClass}` : ``}" id="${l(this.pvdId)}" ?disabled="${this.pvdDisabled}" tabindex="${l(this.pvdDisabled ? '-1' : undefined)}" aria-disabled="${l(this.a11yBoolean(this.pvdDisabled))}" aria-describedby="${l(this.pvdAriaDescribedby)}" aria-label="${l(this.pvdAriaLabel)}">${this.renderLinkContents()}</button>`
            : x `${this.renderDisabledCover()} <a class="pvd-link__link${this.pvdClass ? ` ${this.pvdClass}` : ``}" id="${l(this.pvdId)}" ?disabled="${this.pvdDisabled}" tabindex="${l(this.pvdDisabled ? '-1' : undefined)}" aria-disabled="${l(this.a11yBoolean(this.pvdDisabled))}" aria-describedby="${l(this.pvdAriaDescribedby)}" aria-label="${l(this.pvdAriaLabel)}" href="${l(this.pvdHref)}" target="${l(this.pvdNewWindow ? '_blank' : undefined)}" rel="${l(this.pvdNewWindow ? 'noopener' : undefined)}">${this.renderLinkContents()}</a>`}`;
    }
    render() {
        const classMap = {
            default: `${this.constructor.namespace}-link-root`,
            modifierPrefix: 'pvd-link',
            modifiers: [
                {
                    condition: this.iconOnly,
                    className: 'icon-only',
                },
                {
                    condition: this.hasIconLeft,
                    className: 'icon-left',
                },
                {
                    condition: this.hasIconRight,
                    className: 'icon-right',
                },
                {
                    condition: this.pvdDisabled,
                    className: 'disabled',
                },
                {
                    condition: this.pvdTrigger,
                    className: 'trigger',
                },
                {
                    condition: this.pvdDisableUnderline,
                    className: 'disable-underline',
                },
                {
                    condition: this.hasAttribute('pvd-new-window'),
                    className: 'new-window',
                },
                {
                    condition: true,
                    className: `${this.pvdSize}`,
                },
            ],
        };
        return x `<span class="${getClassName(classMap)}">${this.renderLink()}</span>`;
    }
}
//#region Component configuration
PvdLink.componentName = 'link';
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-id',
    }),
    __metadata("design:type", String)
], PvdLink.prototype, "pvdId", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-class',
    }),
    __metadata("design:type", String)
], PvdLink.prototype, "pvdClass", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-disabled',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-disabled'),
    }),
    __metadata("design:type", Boolean)
], PvdLink.prototype, "pvdDisabled", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-size',
        ...PvdSlotifyBaseComponent.hasChanged(StandardSize),
    }),
    __metadata("design:type", Number)
], PvdLink.prototype, "pvdSize", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-text',
    }),
    __metadata("design:type", String)
], PvdLink.prototype, "pvdText", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-href',
    }),
    __metadata("design:type", String)
], PvdLink.prototype, "pvdHref", void 0);
__decorate([
    n({
        type: String,
        attribute: 'pvd-new-window',
        ...PvdSlotifyBaseComponent.configurablePresenceAttribute(DEFAULT_NEW_WINDOW_STRING),
    }),
    __metadata("design:type", String)
], PvdLink.prototype, "pvdNewWindow", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-icon-right' }),
    __metadata("design:type", String)
], PvdLink.prototype, "pvdIconRight", void 0);
__decorate([
    n({ type: String, reflect: true, attribute: 'pvd-icon-left' }),
    __metadata("design:type", String)
], PvdLink.prototype, "pvdIconLeft", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-trigger',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-trigger'),
    }),
    __metadata("design:type", Boolean)
], PvdLink.prototype, "pvdTrigger", void 0);
__decorate([
    n({
        type: Boolean,
        reflect: true,
        attribute: 'pvd-disable-underline',
        ...PvdSlotifyBaseComponent.booleanAttribute('pvd-disable-underline'),
    }),
    __metadata("design:type", Boolean)
], PvdLink.prototype, "pvdDisableUnderline", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-aria-describedby',
    }),
    __metadata("design:type", String)
], PvdLink.prototype, "pvdAriaDescribedby", void 0);
__decorate([
    n({
        type: String,
        reflect: true,
        attribute: 'pvd-aria-label',
    }),
    __metadata("design:type", String)
], PvdLink.prototype, "pvdAriaLabel", void 0);
__decorate([
    n({ type: Object, attribute: 'pvd-native-anchor-attributes' }),
    __metadata("design:type", Object)
], PvdLink.prototype, "pvdNativeAnchorAttributes", void 0);
__decorate([
    n({ type: Object, attribute: 'pvd-native-button-attributes' }),
    __metadata("design:type", Object)
], PvdLink.prototype, "pvdNativeButtonAttributes", void 0);

export { DEFAULT_DISABLED, DEFAULT_EXTERNAL_LINK_ICON, DEFAULT_HREF, DEFAULT_NEW_WINDOW_STRING, DEFAULT_SIZE, LINK_ANCHOR_PROTECTED_ATTR_MAP, LINK_BUTTON_PROTECTED_ATTR_MAP, PvdLink };
//# sourceMappingURL=pvd-link.js.map
